import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React from 'react';
import Logo from '../logo/Logo';
import FooterItemGrid from './footerItemGrid';
import Divider from '../divider/Divider';
import SocialsGroup from './socialsGroup';
import Image from '../custom_image/Image';
import ButtonGroup from './buttonGroup';
import FooterList from './FooterList';
import QuotingFooter from './QuotingFooter';
import AmpFeaturedSection from './AmpFeaturedSection';
import FooterSkeleton from './FooterSkeleton';
import { useMenuData } from './../../../hooks/useMenuData';

const footerSectionPadding = {
  pl: { xs: '41px', lg: '17.5%' },
  pr: { xs: '41px', lg: '11%' },
  boxSizing: 'border-box',
};

const FooterLogoImages = ({ images = [], isSmallMobile = false, align = 'right' }) => (
  <Box
    className="footer-logo-images"
    sx={{
      display: 'flex',
      alignItems: align === 'left' ? 'flex-start' : 'center',
      justifyContent:
        align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
      flexWrap: 'wrap',
      flexDirection: isSmallMobile && align === 'left' ? 'column' : 'row',
    }}
  >
    {images.map((img, index) => (
      <Box key={img.src || index} sx={{ flexShrink: 0, lineHeight: 0 }}>
        <Image
          alt={img.alt || 'Logo'}
          height="29px"
          src={img.src}
          width="107px"
        />
      </Box>
    ))}
  </Box>
);

const FooterLegalText = ({ html, wrapperSx = {} }) => {
  if (!html?.length) {
    return null;
  }

  return (
    <Box sx={{ ...wrapperSx, maxWidth: { xs: '100%', lg: '70%' } }}>
      <div
        className="footer-legal-text"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Box>
  );
};

const FooterLogoParagraph = ({ html, align = 'right' }) => {
  if (!html?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        width: align === 'center' ? '100%' : undefined,
        flexShrink: align === 'right' ? 0 : undefined,
        textAlign: align,
      }}
    >
      <div
        className="footer-logo-text"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Box>
  );
};

const FooterBottomBar = ({
  horizontalBar = {},
  legalText = '',
  actions,
  isSmallMobile = false,
}) => {
  const images = horizontalBar?.images || [];
  const paragraph = horizontalBar?.paragraph || '';
  const links = horizontalBar?.links || [];
  const hasImages = images.length > 0;
  const hasParagraph = paragraph.length > 0;
  const hasLegalText = legalText?.length > 0;
  const hasLinks = links.length > 0;
  const hasMobileBottomBarContent = hasLegalText || hasImages || hasParagraph;

  if (!hasLinks && !hasLegalText && !hasImages && !hasParagraph) {
    return null;
  }

  return (
    <>
      <Box
        className="footer-desktop-bottom-bar"
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          gap: 2.5,
          width: '100%',
          pt: '46px',
          pb: 3,
          ...footerSectionPadding,
        }}
      >
        {(hasLinks || hasImages) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent:
                hasLinks && hasImages
                  ? 'space-between'
                  : hasImages
                    ? 'flex-end'
                    : 'flex-start',
              gap: 4,
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            {hasLinks && (
              <Box sx={{ alignSelf:'center', flex: hasImages ? 1 : undefined, minWidth: 0 }}>
                <ButtonGroup
                  buttons={links}
                  direction="row"
                  spacing={3}
                  actions={actions}
                />
              </Box>
            )}
            {hasImages && (
              <Box
                className="footer-logo-section-desktop"
                sx={{ flexShrink: 0, maxWidth: '100%' }}
              >
                <FooterLogoImages images={images} />
              </Box>
            )}
          </Box>
        )}

        {(hasLegalText || hasParagraph) && (
          <Box
            className="footer-bottom-text-row"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 4,
              width: '100%',
              flexWrap: 'nowrap',
            }}
          >
            {hasLegalText && (
              <FooterLegalText
                html={legalText}
                wrapperSx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '910px' }}
              />
            )}
            {hasParagraph && (
              <FooterLogoParagraph html={paragraph} align="right" />
            )}
          </Box>
        )}
      </Box>

      {hasMobileBottomBarContent && (
        <Box
          className="footer-mobile-bottom-bar"
          sx={{
            display: { xs: 'flex', lg: 'none' },
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
            mt: 3,
            pb: 3,
            ...footerSectionPadding,
          }}
        >
          {hasLegalText && (
            <FooterLegalText html={legalText} wrapperSx={{ width: '100%' }} />
          )}
          {hasImages && (
            <Box
              className="footer-logo-section-mobile"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
                width: '100%',
              }}
            >
              <FooterLogoImages
                images={images}
                isSmallMobile={isSmallMobile}
                align="left"
              />
            </Box>
          )}
          {hasParagraph && (
            <FooterLogoParagraph html={paragraph} align="left" />
          )}
        </Box>
      )}
    </>
  );
};

/**
* Footer component for rendering a custom footer with logo, links, buttons, and legal text.
* The layout and content of the footer adapt based on screen size using Material-UI's
* responsive utilities.
*/
export default function Footer({ actions }) {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('lm'));

  // Get and format footer menu data reactively - component updates when data arrives
  const footerMenu = useMenuData('footer_menu');
  const { columns, mobileSections, socialIcons, horizontalBar, legalText } = footerMenu;
  
  // Check if menu data is loaded (has actual content, not just empty state)
  const isMenuLoaded = columns.length > 0 || mobileSections.length > 0;
  
  const shouldRenderQuotingFooter = window.location.pathname.includes('/quoting');
  const shouldRenderAmpFeaturedSection = window.location.pathname.includes('/account-summary');

  // Show logo immediately for branding; skeleton only the menu-dependent content
  if (!isMenuLoaded) {
    return (
      <FooterSkeleton
        logo={
          <>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Logo alt="Custom Footer Logo" logoType="footer" logoheight="56px" logowidth="273px" />
            </Box>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Logo alt="Custom Footer Logo" logoType="footer" logoheight="50px" logowidth="244px" />
            </Box>
          </>
        }
      />
    );
  }

  return (
    <>
      {shouldRenderAmpFeaturedSection && <AmpFeaturedSection />}
      {shouldRenderQuotingFooter && <QuotingFooter />}
      <Box
        maxWidth="100%"
        disableGutters
        sx={{ backgroundColor: theme.palette.primary.main, overflow: 'hidden' }}
      >
        <Container maxWidth="100%" sx={{ p: "0 !important" }} >
          <Box
            sx={{
              [theme.breakpoints.up('md')]: { // This will apply the styles only on desktop screens (md and up)
                display: 'flex',
                flexDirection: 'column',
                pt: '66px',
                width: '100%',
                pb: '50px',
                position: 'relative',
              },
              [theme.breakpoints.up('xs')]: {
                pt: '50px',
              },
              pl: { xs: '26px', lg: '17.5%' },
              pr: { xs: '26px', lg: '11%' },
              boxSizing: "border-box"
            }}
          >
            <Box sx={{ display: 'inline-block', verticalAlign: 'top', width: '100%' }}>
              {/*For Desktop #Calcas Logo*/}
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Logo
                  alt="Custom Footer Logo"
                  logoType="footer"
                  logoheight="56px"
                  logowidth="273px"
                />
              </Box>
              {/*For Mobile #Calcas Logo*/}
              <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <Logo
                  alt="Custom Footer Logo"
                  logoType="footer"
                  logoheight="50px"
                  logowidth="244px"
                />
              </Box>
              {/*For Desktop #Vertical Menu*/}
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <FooterItemGrid columns={columns} />
              </Box>
              {/*For Mobile #Vertical Menu*/}
              <Box sx={{ display: { xs: 'block', lg: 'none' }, pt: '46px', pb: '56px' }}>
                <FooterList actions={actions} backgroundColor="transparent" sections={mobileSections} />
              </Box>
              {/*For Desktop #Social Icons*/}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'flex-end',
                  width: '100%',
                  marginTop: '-55px',
                  position: 'relative'
                }}
              >
                <SocialsGroup color="#68D3E3" size="34px" socialIcons={socialIcons} />
              </Box>
              {/*For Mobile #Social Icons*/}
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  justifyContent: 'center',

                }}
              >
                <SocialsGroup
                  color="#68D3E3"
                  socialIcons={socialIcons}
                  size={isSmallMobile ? '20px' : '25px'}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: 'block', lg: 'none' },
                }}
              >
                <ButtonGroup buttons={horizontalBar?.links || []} direction="column" actions={actions} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: "34px" } }}>
            <Divider backgroundColor="#FFFFFF" height=".5px" width="100%" />
          </Box>
          <Box
            sx={{
              [theme.breakpoints.down('md')]: { // Apply only for xs and smaller devices
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                py: { xs: 0 }, // Set vertical padding to 0 for xs
                padding: '48px 0 !important', // Apply padding for xs
                position: 'relative',
              }
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '100%',
              }}
            >
              <FooterBottomBar
                horizontalBar={horizontalBar}
                legalText={legalText}
                actions={actions}
                isSmallMobile={isSmallMobile}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
